/**
 * Vorzyklus-Diff helpers for the Economy screen. Given two EconomySnapshot
 * entries (previous cycle + current cycle), produce render-ready delta
 * descriptors that can be injected into KPI cards and balance rows.
 *
 * The server emits per-field snapshots in Player.EconomyHistory; the
 * client fetches these via getEconomyHistory(sessionId) on screen mount
 * and caches them across tab switches.
 */
import type { EconomySnapshot } from '../api';

export interface DeltaDescriptor {
  /** +X (increase) or -X (decrease). 0 when no change. */
  value: number;
  /** 'up' | 'down' | 'flat' — drives icon + color. */
  direction: 'up' | 'down' | 'flat';
  /** true when the direction is favourable for the player (more income = good, more cost = bad). */
  positive: boolean;
}

/** Describe the change from `prev` to `curr` for the given field.
 *  For "bad" fields (expenses, dirty flow), positivity flips. */
export function describeDelta(
  prev: number,
  curr: number,
  invertPositivity = false,
): DeltaDescriptor {
  const diff = curr - prev;
  const direction: DeltaDescriptor['direction'] =
    diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat';
  const positive = invertPositivity ? diff < 0 : diff > 0;
  return { value: diff, direction, positive };
}

/** Find the previous cycle's snapshot relative to `current`. Returns the
 *  second-to-last entry when the history has ≥ 2 snapshots; null otherwise. */
export function previousSnapshot(
  history: readonly EconomySnapshot[],
): EconomySnapshot | null {
  if (history.length < 2) return null;
  return history[history.length - 2];
}

/** Format a delta as HTML string with arrow + color class. Caller inlines. */
export function renderDeltaHTML(
  d: DeltaDescriptor,
  formatValue: (v: number) => string,
): string {
  if (d.direction === 'flat') {
    return `<span class="eco-diff eco-diff-flat" title="Unverändert zum Vortag">\u2013</span>`;
  }
  const arrow = d.direction === 'up' ? '\u25B2' : '\u25BC';
  const colorClass = d.positive ? 'eco-diff-pos' : 'eco-diff-neg';
  const sign = d.value > 0 ? '+' : '-';
  return `<span class="eco-diff ${colorClass}" title="vs. Vortag">${arrow} ${sign}${formatValue(Math.abs(d.value))}</span>`;
}
